import { EulaDomain, EulaSection, EulaTaxonomy, ExperienceFragmentResponse } from '../types';
import extractParagraphs from './fragmentParser';

const ROMAN_HEADING_REGEX = /^([IVXLCDM]+)\.\s*(.+)$/;
const SECTION_HEADING_REGEX = /^(\d+)\.\s*(.+)$/;
const SUBSECTION_REGEX = /^(\d+(?:\.\d+)+)\.\s*(.+)$/;
const DEFINITION_REGEX =
  /^[“"']?\s*(.+?)\s*[”"']?\s+(means|shall mean|has the meaning of|includes|refers to)\s+(.+)$/i;

function toDomainKey(title: string): string {
  const base = title
    .replace(/\(.*?\)/g, ' ')
    // \p{L} = any letter (Japanese, Cyrillic, etc.)
    // \p{N} = any number
    // 'u' at the end is essential to enable Unicode support
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

  return base ? base.split(/\s+/).join('_').toUpperCase() : 'DOMAIN';
}

function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function stripTrailingPeriod(value: string): string {
  return value.replace(/\.+$/, '').trim();
}

function findOrCreateSection(domain: EulaDomain, sectionNumber: string): EulaSection {
  let section = domain.sections.find((item) => item.number === sectionNumber);
  if (!section) {
    section = { number: sectionNumber, paragraphs: [] };
    domain.sections.push(section);
  }
  return section;
}

export function buildEulaTaxonomy(raw: ExperienceFragmentResponse): EulaTaxonomy {
  const paragraphs = extractParagraphs(raw);
  if (!paragraphs.length) {
    throw new Error('EULA document is empty');
  }

  const taxonomy: EulaTaxonomy = {
    title: paragraphs[0],
    preamble: [],
    domains: [],
  };

  let startIndex = 1;

  if (paragraphs[1] && /^Updated:/i.test(paragraphs[1])) {
    taxonomy.updatedOn = paragraphs[1].replace(/^Updated:\s*/i, '').trim();
    startIndex = 2;
  }

  let currentDomain: EulaDomain | undefined;
  let currentSection: EulaSection | undefined;

  for (let i = startIndex; i < paragraphs.length; i += 1) {
    const paragraph = paragraphs[i];

    const romanMatch = paragraph.match(ROMAN_HEADING_REGEX);
    if (romanMatch) {
      const [, ordinal, titleBody] = romanMatch;
      const rawTitle = stripTrailingPeriod(titleBody);
      const formattedTitle = toTitleCase(rawTitle);
      const domain: EulaDomain = {
        key: toDomainKey(rawTitle),
        ordinal: ordinal.trim(),
        title: formattedTitle,
        rawTitle,
        intro: [],
        sections: [],
      };

      taxonomy.domains.push(domain);
      currentDomain = domain;
      currentSection = undefined;
      continue;
    }

    if (!currentDomain) {
      taxonomy.preamble.push(paragraph);
      continue;
    }

    if (currentDomain.key === 'DEFINITIONS') {
      const definitionMatch = paragraph.match(DEFINITION_REGEX);
      if (definitionMatch) {
        const [, rawTerm, verb, remainder] = definitionMatch;
        const title = rawTerm.replace(/[“”"']/g, '').trim();
        const text = `${verb} ${remainder}`.trim();
        currentDomain.intro.push({ title, text });
        currentSection = undefined;
        continue;
      }
    }

    const subsectionMatch = paragraph.match(SUBSECTION_REGEX);
    if (subsectionMatch) {
      const [, ref, content] = subsectionMatch;
      const sectionNumber = ref.split('.')[0];
      const section = findOrCreateSection(currentDomain, sectionNumber);
      section.paragraphs.push({
        number: ref,
        text: content.trim(),
      });
      currentSection = section;
      continue;
    }

    const sectionMatch = paragraph.match(SECTION_HEADING_REGEX);
    if (sectionMatch) {
      const [, number, rest] = sectionMatch;
      const headingMatch = rest.match(/^(.*?\.)\s+(.*)$/);
      let title = rest.trim();
      let remainder: string | undefined;

      if (headingMatch) {
        title = headingMatch[1].trim();
        remainder = headingMatch[2].trim();
      }

      const section: EulaSection = {
        number,
        title: stripTrailingPeriod(title) || undefined,
        paragraphs: [],
      };

      currentDomain.sections.push(section);
      currentSection = section;

      if (remainder) {
        section.paragraphs.push({ text: remainder });
      }

      continue;
    }

    if (!currentSection) {
      currentDomain.intro.push({ text: paragraph });
      continue;
    }

    currentSection.paragraphs.push({ text: paragraph });
  }

  return taxonomy;
}
